import React from 'react';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import PublicPage, { COMPANY, SUPPORT_EMAIL, SUPPORT_PHONE } from './Public/PublicPage';

// Public privacy policy (linked from the Zoom Marketplace listing and the apps).
const PrivacyPolicy = () => (
  <PublicPage title="Privacy Policy" updated="5 October 2026" icon={<ShieldRoundedIcon sx={{ fontSize: 40, color: '#38bdf8' }} />}>
    <p>
      This policy explains what personal information ZPro Audio collects, why, how long we keep it and the rights you have.
      ZPro Audio is provided by <strong>{COMPANY}</strong> ("we", "us"). It covers the ZPro Audio Android app, the ZPro Audio
      Windows app and the web panel at zt.zaudio.us.
    </p>

    <h2>1. Who uses ZPro Audio</h2>
    <p>
      ZPro Audio is sold to organisations such as schools, coaching classes and teams. An organisation's <strong>admin</strong> creates
      accounts for its <strong>hosts</strong> (who run rooms) and <strong>participants</strong> (who join rooms). For participants and hosts,
      the organisation decides what information is entered about them; we process it on the organisation's behalf.
    </p>

    <h2>2. Information we collect</h2>
    <table>
      <thead><tr><th>Information</th><th>Why</th></tr></thead>
      <tbody>
        <tr><td>Name, username, phone number, optional email, password (stored only as a hash), room and account expiry date — entered by the admin</td><td>To let you log in, put you in the right room and end access when the account expires</td></tr>
        <tr><td>A device identifier of the phone you log in from</td><td>To keep each participant account on one phone</td></tr>
        <tr><td>Login sessions, whether you are in a meeting, muted or speaking, and when you were last online</td><td>To show the host who is present and talking, and to enforce one session per device</td></tr>
        <tr><td>Messages the admin or host posts to a room</td><td>To show them to the room</td></tr>
        <tr><td><strong>Zoom data</strong>, only for admins who click "Connect Zoom": Zoom OAuth access and refresh tokens, Zoom user and account IDs, each room's Zoom meeting number and join details, and short-lived start/join tokens</td><td>To create each room's Zoom meeting, let the host start it, let participants join it and list the room's cloud recordings</td></tr>
      </tbody>
    </table>

    <h3>What we do not collect</h3>
    <ul>
      <li><strong>Audio:</strong> meeting audio is carried by Zoom. Our servers do not receive or store it.</li>
      <li><strong>Voice clips on your phone:</strong> the Android app can keep a copy of each of your own hold-to-talk turns <em>on your phone only</em> so you can listen back. They are never uploaded, and you can delete them in the app.</li>
      <li><strong>Host recordings on a PC:</strong> the Windows host app can save a backup recording of the meeting <em>on that PC only</em> (the meeting is announced as recorded). It is never uploaded to us.</li>
      <li><strong>Zoom cloud recordings</strong> stay in the organisation's own Zoom account. The web panel lists and plays them directly from Zoom; we do not copy them.</li>
      <li>We do not collect location, contacts or advertising identifiers, and we do not use your data for advertising.</li>
    </ul>

    <h2>3. Microphone</h2>
    <p>
      The apps use the microphone only to send your voice into the Zoom meeting: for participants while you hold the talk button,
      for hosts while their mic is switched on.
    </p>

    <h2>4. Who we share information with</h2>
    <ul>
      <li><strong>Zoom Video Communications</strong>, which runs the meetings and recordings under the organisation's own Zoom account and Zoom's own privacy statement.</li>
      <li><strong>Our hosting provider</strong>, which runs the server that stores the information above.</li>
      <li><strong>Authorities</strong>, when the law requires it.</li>
    </ul>
    <p>We do not sell or rent personal information.</p>

    <h2>5. How long we keep it</h2>
    <ul>
      <li>Account information is kept while the organisation uses ZPro Audio. When an admin deletes an account it can no longer be used; you or the admin can ask us to erase it completely.</li>
      <li>Zoom tokens are deleted as soon as the admin clicks <strong>Disconnect</strong> in the panel or removes ZPro Audio from their Zoom account. When we receive Zoom's deauthorization notice we delete that admin's Zoom tokens immediately.</li>
      <li>On-phone voice clips and PC backup recordings stay on that device until you delete them.</li>
    </ul>

    <h2>6. Security</h2>
    <p>
      All traffic between the apps, the panel and our server is encrypted with TLS 1.2 or newer. Passwords are stored as hashes,
      the database is not reachable from the internet, logins are rate-limited and accounts are blocked automatically when they
      expire or are deactivated.
    </p>

    <h2>7. Your rights</h2>
    <p>
      You can ask us to <strong>see</strong> the personal information we hold about you, <strong>correct</strong> it, <strong>delete</strong> it,
      <strong> receive a copy</strong> of it, or <strong>object to or restrict</strong> how we use it. Depending on where you live (for example
      under the GDPR or US state privacy laws) you may have further rights, including complaining to your data protection authority.
    </p>
    <p>
      To use any of these rights, email <a href={'mailto:' + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a> from the email or phone number on your
      account, or ask your organisation's admin. We reply within 30 days. If your account belongs to an organisation, we may pass your
      request to its admin.
    </p>

    <h2>8. Children</h2>
    <p>
      Organisations that let students under 13 use ZPro Audio are responsible for getting any consent the law requires before creating
      their accounts. We do not knowingly collect information from children in any other way.
    </p>

    <h2>9. Changes</h2>
    <p>If we change this policy we will update this page and the date at the top.</p>

    <h2>10. Contact</h2>
    <p>
      {COMPANY}<br />
      Email: <a href={'mailto:' + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a><br />
      Phone: <a href={'tel:' + SUPPORT_PHONE.replace(/[^+\d]/g, '')}>{SUPPORT_PHONE}</a>
    </p>
  </PublicPage>
);

export default PrivacyPolicy;
